<template>
  <div class="hryContainer">
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="primary"
      size="48"
      class="mx-auto my-8"
      aria-label="Loading skill hierarchies"
      role="status"
    ></v-progress-circular>
    <template v-else>
      <!-- Scrollable Content Area -->
      <div class="scrollable-content">
        <!-- Hierarchy and Classifier Panels -->
        <v-expansion-panels 
          v-if="newUi" 
          multiple
          accordion
          flat 
          tile
          v-model="expanded"
          @change="onExpansionChange"
        >
          <v-expansion-panel v-for="(c, ic) in classifiers" :key="'c' + ic">
            <v-expansion-panel-header disable-icon-rotate>
              <template v-slot:default="{ open }">
                <div class="full-width-header">
                  <div class="header-left">
                    <v-icon class="chevron-icon" :class="{ 'rotated': open }">
                      mdi-chevron-right
                    </v-icon>
                    <span class="header-title">{{ c.ct_name }}</span>
                  </div>
                  <div class="header-right">
                    <v-btn
                      v-if="c.selected && c.selected.length > 0"
                      text
                      x-small
                      class="clear-selection-btn"
                      @click.stop="clearClassifierSelection(ic)"
                      :title="`Clear ${c.ct_name} selections`"
                    >
                      Clear
                      <v-chip x-small class="count-badge">{{ c.selected.length }}</v-chip>
                      <v-icon x-small class="clear-icon">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-treeview
                :ref="'ctv' + ic"
                dense
                selectable
                return-object
                item-key="cv_id"
                open-on-click
                :items="c.items"
                v-model="c.selected"
                :class="['treeview' + ic, 'newUi']"
                @input="doFilter"
              >
                <template v-slot:label="{ item }">
                  <span :title="item.value">
                    {{ item.value }} ( {{ item.count }} )
                  </span>
                </template>
              </v-treeview>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel v-for="(hry, ih) in hierarchies" :key="'h' + ih">
            <v-expansion-panel-header disable-icon-rotate>
              <template v-slot:default="{ open }">
                <div class="full-width-header">
                  <div class="header-left">
                    <v-icon class="chevron-icon" :class="{ 'rotated': open }">
                      mdi-chevron-right
                    </v-icon>
                    <span class="header-title">{{ hry.label }}</span>
                  </div>
                  <div class="header-right">
                    <v-btn
                      v-if="hry.selected && hry.selected.length > 0"
                      text
                      x-small
                      class="clear-selection-btn"
                      @click.stop="clearHierarchySelection(ih)"
                      :title="`Clear ${hry.label} selections`"
                    >
                      <span style="padding-bottom: 3px;">Clear</span>
                      <v-chip x-small class="count-badge">{{ getHierarchyDocumentCount(hry) }}</v-chip>
                      <v-icon x-small class="clear-icon">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="hierarchy-filter-container">
                <v-text-field
                  dense
                  outlined
                  clearable
                  placeholder="Search hierarchy..."
                  :value="hry.filterText"
                  @keypress.enter="triggerTreeFilter(ih)"
                  @input="(v) => changeTreeFilterText(hry, v)"
                  @click:clear="clearTreeFilterText(ih)"
                  prepend-inner-icon="mdi-magnify"
                  :class="'hierarchy-filter-input filter' + ih"
                  @click:append="clearTreeFilters(ih)"
                  hide-details
                ></v-text-field>
              </div>
              <v-treeview
                :ref="'tv' + ih"
                dense
                selectable
                return-object
                item-key="id"
                open-on-click
                :items="hry.items"
                v-model="hry.selected"
                :search="hry.filterText"
                :filter="filterHryItems"
                :class="['treeview' + ih, 'newUi']"
                @input="treeviewInput(ih, $event)"
                item-disabled="disabled"
              >
                <template v-slot:label="{ item }">
                  <span 
                    :title="item.name" 
                    v-html="item.name"
                    :class="{ 'disabled-hierarchy-item': item.disabled }"
                  ></span>
                </template>
              </v-treeview>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Document Types Filter Panel -->
          <v-expansion-panel>
            <v-expansion-panel-header disable-icon-rotate>
              <template v-slot:default="{ open }">
                <div class="full-width-header">
                  <div class="header-left">
                    <v-icon class="chevron-icon" :class="{ 'rotated': open }">
                      mdi-chevron-right
                    </v-icon>
                    <span class="header-title">Document Types</span>
                  </div>
                  <div class="header-right">
                    <v-btn
                      v-if="docTypeFilters && docTypeFilters.length > 0"
                      text
                      x-small
                      class="clear-selection-btn"
                      @click.stop="clearDocTypeFilters"
                      title="Clear Document Type filters"
                    >
                      <span style="padding-bottom: 3px;">Clear</span>
                      <v-chip x-small class="count-badge">{{ docTypeFilters.length }}</v-chip>
                      <v-icon x-small class="clear-icon">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="filter-checkbox-list">
                <v-checkbox
                  v-for="docType in availableDocTypes"
                  :key="docType.docType"
                  v-model="docTypeFilters"
                  :value="docType.docType"
                  :label="`${docType.docType} (${docType.count})`"
                  :disabled="docType.disabled"
                  dense
                  hide-details
                  class="filter-checkbox-item"
                  :class="{ 'disabled-filter-item': docType.disabled }"
                  @change="doFilter"
                  color="#82b1ff"
                >
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Document Status Filter Panel -->
          <v-expansion-panel>
            <v-expansion-panel-header disable-icon-rotate>
              <template v-slot:default="{ open }">
                <div class="full-width-header">
                  <div class="header-left">
                    <v-icon class="chevron-icon" :class="{ 'rotated': open }">
                      mdi-chevron-right
                    </v-icon>
                    <span class="header-title">Document Status</span>
                  </div>
                  <div class="header-right">
                    <v-btn
                      v-if="docStatusFilters && docStatusFilters.length > 0"
                      text
                      x-small
                      class="clear-selection-btn"
                      @click.stop="clearDocStatusFilters"
                      title="Clear Document Status filters"
                    >
                      <span style="padding-bottom: 3px;">Clear</span>
                      <v-chip x-small class="count-badge">{{ docStatusFilters.length }}</v-chip>
                      <v-icon x-small class="clear-icon">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="filter-checkbox-list">
                <v-checkbox
                  v-for="status in availableDocStatuses"
                  :key="status.status"
                  v-model="docStatusFilters"
                  :value="status.status"
                  :label="`${status.status} (${status.count})`"
                  :disabled="status.disabled"
                  dense
                  hide-details
                  class="filter-checkbox-item"
                  :class="{ 'disabled-filter-item': status.disabled }"
                  @change="doFilter"
                >
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <!-- Fixed Footer with Action Buttons -->
      <div class="fixed-footer">
        <div class="footer-controls">
          <v-btn
            depressed
            small
            :disabled="!hasActiveFilters && !hasActiveHierarchySelections"
            @click="clearAll"
            class="clear-btn"
            title="Clear all filters and selections"
          >
            <v-icon left>mdi-close</v-icon>
            Clear All Filters
          </v-btn>
          <v-btn
            color="success"
            depressed
            small
            :disabled="selectedDocumentIds.length === 0"
            @click="emitExploreDocuments"
            aria-label="Explore Skill for selected documents"
            class="explore-btn"
          >
            Explore Skills ({{ selectedDocumentIds.length }})
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import utils from "@/common/utils.js";
import { mapState } from "vuex";

export default {
  name: "HierarchySearch",
  components: {},
  props: {
    items: null,
    preSelected: null,
    preSelectedClassifiers: null,
    hierarchyTypes: null,
    classifierTypes: null,
    framework: null
  },
  data: function () {
    return {
      hierarchies: [],
      classifiers: [],
      expanded: [],
      selectionChanged: false,
      refreshing: false,
      showDocFilters: false,
      docTypeFilters: [],
      docStatusFilters: [],
      loading: true,
      selectedDocs: [], // Track selected documents
      initialAutoSelectionDone: false, // Track if initial auto-selection has been applied
      preventExpansionChange: false, // Prevent expansion panel changes during treeview interactions
      // Performance optimization caches
      filterCache: new Map(),
      hierarchyMatchCache: new Map(),
      lastCacheKey: '',
      debounceTimer: null,
      availableDocTypes: [],
      availableDocStatuses: [],
      selectedDocuments: [],
    };
  },
  created() {
    // Always build hierarchies/classifiers, even if items is empty
    this.buildClassifiers();
    this.buildHierarchies();
    // Set initial document type filter based on framework
    this.setInitialDocTypeFilter();
    this.refreshFilterCounts();
  },
  beforeDestroy() {
    // Clean up timers and caches to prevent memory leaks
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.clearFilterCache();
  },
  watch: {
    items() {
      if (!this.useIndex) {
        // Reset auto-selection flag when items change to allow new auto-selection
        this.initialAutoSelectionDone = false;
        this.buildClassifiers();
        this.buildHierarchies();
        this.refreshFilterCounts();
      }
    },
    hierarchyTypes() {
      this.buildClassifiers();
      this.buildHierarchies();
      this.refreshFilterCounts();
    },
    framework: {
      handler() {
        // Set document type filter when framework changes
        this.setInitialDocTypeFilter();
      },
      deep: true,
      immediate: true
    },
    docTypeFilters: {
      handler() {
        // Clear cache and force update when document type filters change
        this.clearFilterCache();
        this.$nextTick(() => {
          // Force reactivity update for computed properties
          this.$forceUpdate();
        });
      },
      deep: true
    },
    docStatusFilters: {
      handler() {
        // Clear cache and force update when document status filters change
        this.clearFilterCache();
        this.$nextTick(() => {
          // Force reactivity update for computed properties
          this.$forceUpdate();
        });
      },
      deep: true
    },
  },
  computed: {
    ...mapState({
      newUi: (state) => state.theme.newUi,
      vuexHierarchyTypes: (state) => state.docs?.hierarchyTypes || state.hierarchyTypes || [],
      vuexClassifierTypes: (state) => state.docs?.classifierTypes || state.classifierTypes || [],
      vuexItems: (state) => state.docs?.docsList || state.items || state.docsList || [],
      useIndex: (state) => state.docs.useIndex,
      docStatusSummary: (state) => state.docs.docStatusSummary,
      docTypeSummary: (state) => state.docs.docTypeSummary,
      hierarchyLookup: (state) => state.hierarchies.hierarchyLookup,
      docClassifierLookup: (state) => state.hierarchies.docClassifierLookup,
    }),
    effectiveHierarchyTypes() {
      return this.hierarchyTypes && this.hierarchyTypes.length ? this.hierarchyTypes : this.vuexHierarchyTypes || [];
    },
    effectiveClassifierTypes() {
      return this.classifierTypes && this.classifierTypes.length ? this.classifierTypes : this.vuexClassifierTypes || [];
    },
    effectiveItems() {
      // Always prioritize store data for building hierarchy structure
      // Only use passed items for auto-selection logic
      return this.vuexItems && this.vuexItems.length ? this.vuexItems : this.items || [];
    },
    filteredItems() {
      // Filter documents by selected docTypeFilters and docStatusFilters
      let items = this.effectiveItems || [];
      if (this.docTypeFilters.length > 0) {
        items = items.filter(doc => this.docTypeFilters.includes(doc.doc_type));
      }
      if (this.docStatusFilters.length > 0) {
        items = items.filter(doc => this.docStatusFilters.includes(doc.lifecycle_status_name));
      }
      return items;
    },
    docTypesLabel() {
      return `Document Types (${this.docTypeFilters.length})`;
    },
    docStatusesLabel() {
      return `Document Statuses (${this.docStatusFilters.length})`;
    },
    hasActiveFilters() {
      return this.docTypeFilters.length > 0 || this.docStatusFilters.length > 0;
    },
    hasActiveHierarchySelections() {
      return this.hierarchies.some(hry => hry.selected && hry.selected.length > 0) ||
             this.classifiers.some(classifier => classifier.selected && classifier.selected.length > 0);
    },
    allDocTypesSelected() {
      return (
        this.docTypeFilters.length > 0 &&
        this.docTypeFilters.length === this.availableDocTypes.length
      );
    },
    allDocStatusesSelected() {
      return (
        this.docStatusFilters.length > 0 &&
        this.docStatusFilters.length === this.availableDocStatuses.length
      );
    },
    selectedDocumentIds() {
      // Return document IDs from the selected documents
      return this.selectedDocuments.map(doc => doc.doc_id);
    },
    documentTree() {
      // Group documents by hierarchy for treeview, documents as leaf nodes
      const tree = [];
      const hierarchyMap = {};
      (this.filteredItems || []).forEach(doc => {
        const hierarchyKey = doc.hierarchy_path ? doc.hierarchy_path.join(' > ') : 'Unclassified';
        if (!hierarchyMap[hierarchyKey]) {
          hierarchyMap[hierarchyKey] = {
            id: hierarchyKey,
            name: hierarchyKey,
            children: [],
          };
          tree.push(hierarchyMap[hierarchyKey]);
        }
        hierarchyMap[hierarchyKey].children.push({
          ...doc,
          id: doc.doc_id,
          name: doc.name || doc.doc_name || doc.doc_id,
        });
      });
      return tree;
    },
    filters() {
      const hierarchyFilters = this.hierarchies
        .map(h => ({
          ht_id: h.ht_id,
          selected: h.selected.map(hr => ({ id: hr.id }))
        }))
        .filter(h => h.selected.length);
      const classifierFilters = this.classifiers
        .map(c => ({
          ct_id: c.ct_id,
          selected: c.selected.map(cv => cv.cv_id)
        }))

      return {
          docTypeIncluded: this.docTypeFilters,
          docStatusIncluded: this.docStatusFilters,
          hierarchyFilters: hierarchyFilters,
          classifierFilters: classifierFilters, 
      }
    }
  },
  methods: {
    buildClassifiers() {
      if (!this.effectiveItems || !this.effectiveClassifierTypes || !this.effectiveClassifierTypes.length)
        return;

      if (!this.classifiers.length && this.effectiveClassifierTypes) {
        this.classifiers = this.effectiveClassifierTypes.map((ct) => {
          return {
            ct_id: ct.ct_id,
            ct_name: ct.ct_name,
            items: [],
            selected: [],
          };
        });
      }

      const preSelectedClassifiers = [];

      this.classifiers.forEach((c) => {
        if (this.preSelectedClassifiers) {
          const preSelectedProp = this.preSelectedClassifiers.find(
            (s) => s.ct_id === c.ct_id
          );
          preSelectedClassifiers.push(
            preSelectedProp
              ? JSON.parse(JSON.stringify(preSelectedProp.selected))
              : []
          );
        } else {
          preSelectedClassifiers.push(JSON.parse(JSON.stringify(c.selected)));
        }
        c.items.splice(0);
        c.selected.splice(0);
      });

      // Use ALL items to build classifier structure, then update counts
      this.effectiveItems.forEach((d) => {
        this.classifiers.forEach((c) => {
          if (d.classifiers) {
            let docc = d.classifiers.find((dc) => dc.ct_id === c.ct_id);
            this.decorateClassifier(c.items, docc);
          }
        });
      });

      // Update counts based on cross-filtering
      this.classifiers.forEach((c) => {
        this.updateClassifierCounts(c);
      });

      this.classifiers.forEach((c, i) => {
        this.sortHierarchy(c.items);
        c.selected = preSelectedClassifiers[i];
      });
      this.loading = false;
    },
    async buildHierarchies() {
      if ((!this.useIndex && !this.effectiveItems) || !this.effectiveHierarchyTypes || !this.effectiveHierarchyTypes.length) {
        return;
      }

      let preSelected = [];

      if (!this.hierarchies.length) {
        this.hierarchies = this.effectiveHierarchyTypes.map((h) => {
          let levels = Array(
            h.use_h1 + h.use_h2 + h.use_h3 + h.use_h4 + h.use_h5 + h.use_h6
          ).fill(0);
          return {
            ht_id: h.ht_id,
            label: h.label,
            linklevel: h.linklevel,
            cols: levels.map((x, i) => "hierarchy" + (levels.length - i)),
            items: [],
            open: [],
            selected: [],
            filterText: "",
            tempText: "",
          };
        });
      }
      
      this.hierarchies.forEach((h, idx) => {
        // Check for explicit preSelected prop from parent component (passedParams)
        if (Array.isArray(this.preSelected)) {
          const preSelectedProp = this.preSelected.find(
            (s) => s && s.ht_id === h.ht_id && Array.isArray(s.selected)
          );
          if (preSelectedProp) {
            // Explicit preselection exists
            preSelected.push(JSON.parse(JSON.stringify(preSelectedProp.selected)));
          } else {
            // No explicit preselection for this hierarchy type
            preSelected.push([]);
          }
        } else {
          // No preSelected prop = empty array (no auto-selection)
          preSelected.push([]);
        }
        this.clearHierarchy(h.items);
        this.clearHierarchy(h.open);
      });

      // Build hierarchy items - USE all hierarchy items
      Object.keys(this.hierarchyLookup).forEach(k => {
        const doch = this.hierarchyLookup[k];
        const hierarchy = this.hierarchies.find(h => h.ht_id === doch.ht_id);
        if (doch && hierarchy) this.decorateHierarchy(hierarchy.items, hierarchy.cols, doch, 0);
      });
      
      this.hierarchies.forEach((h) => {
        this.sortHierarchy(h.items);
      });
      
      this.tvIgnoreInputEvent = true;
      this.hierarchies.forEach((h, i) => {
        if (Array.isArray(preSelected) && preSelected[i] && preSelected[i].length > 0) {
          // Find matching items in the rebuilt hierarchy
          const matchingItems = [];
          preSelected[i].forEach(preItem => {
            const found = this.findItemInHierarchy(h.items, preItem.id);
            if (found) {
              matchingItems.push(found);
            }
          });
          h.selected = matchingItems;
        } else if (this.items && this.items.length > 0 && !this.initialAutoSelectionDone) {
          // Auto-select hierarchy items ONLY on initial load when specific documents are passed via items prop
          // Only auto-select if this hierarchy matches the framework ht_id
          if (this.framework && this.framework.ht_id && h.ht_id === this.framework.ht_id) {
            const autoSelectedItems = this.findHierarchyItemsForDocuments(h);
            if (autoSelectedItems.length > 0) {
              h.selected = autoSelectedItems;
            } else {
              h.selected = [];
            }
          } else {
            // For non-matching hierarchy types, start with empty selection
            h.selected = [];
          }
        } else {
          // Preserve existing selections during rebuilds, or start with empty if no previous selections
          if (this.initialAutoSelectionDone) {
            // Keep existing selections by finding them in the rebuilt hierarchy
            const existingSelections = [];
            h.selected.forEach(selectedItem => {
              const found = this.findItemInHierarchy(h.items, selectedItem.id);
              if (found) {
                existingSelections.push(found);
              }
            });
            h.selected = existingSelections;
          } else {
            h.selected = [];
          }
        }
      });

      // Only auto-expand on initial load, not during subsequent rebuilds
      const shouldAutoExpand = !this.initialAutoSelectionDone;

      // Mark that initial auto-selection has been done
      if (!this.initialAutoSelectionDone) {
        this.initialAutoSelectionDone = true;
      }

      // Auto-expand only on initial load
      if (shouldAutoExpand) {
        const firstSelectedHierarchyIdx = this.hierarchies.findIndex(
          (h) => h.selected.length
        );
        if (
          this.hierarchies.length > 0 && 
          this.expanded < this.classifiers.length + this.hierarchies.length &&
          this.hierarchies[this.expanded - this.classifiers.length] &&
          this.hierarchies[this.expanded - this.classifiers.length].selected.length == 0 &&
          firstSelectedHierarchyIdx > -1
        ) {
          this.expanded = this.classifiers.length + firstSelectedHierarchyIdx;
        }
      }

      this.refreshing = this.selectionChanged;
      this.selectionChanged = false;
      this.loading = false;
    },
    clearHierarchy(items) {
      items.forEach((c) => {
        if (c.children && c.children.length) {
          this.clearHierarchy(c.children);
        }
        delete c.children;
      });
      items.length = 0;
    },
    decorateHierarchy(topitem, levels, item, count) {
      let current = topitem;
      let parent;
      levels.forEach((l, i) => {
        let value = utils.removeTags(item[levels[i]] || "UNCLASSIFIED").trim(),
          h = current.find((l) => l.value === value),
          includeChildren = i < levels.length - 1;
        if (!h) {
          // Ensure hr_id is always a string
          const hrIdString = String(item.hr_id || '');
          h = {
            // For leaf nodes (last level), use the exact hr_id without suffix
            // For parent nodes, use hr_id with level suffix to differentiate
            id: includeChildren ? hrIdString + "_" + i : hrIdString,
            name: "",
            count: 0,
            value: value,
            parent: parent,
            // Mark if this is a leaf node (bottom level)
            isLeaf: !includeChildren,
            // Store the original hr_id for leaf nodes (always as string)
            hr_id: includeChildren ? null : hrIdString
          };
          if (includeChildren) h.children = [];
          current.push(h);
        }
        h.count += count;
        h.name = h.value + " (" + h.count + ")";
        current = h.children;
        parent = h;
      });
    },
    decorateClassifier(topitem, item) {
      const value = (item?.value || "UNCLASSIFIED").trim();
      const cv_id = item?.cv_id || 0;
      const c = topitem.find((v) => v.cv_id === cv_id);
      if (c) {
        c.count++;
      } else {
        topitem.push({ cv_id: cv_id, value: value, count: 1 });
      }
    },
    sortHierarchy(h) {
      if (h && h.length > 0) {
        h.sort((a, b) => (a.value > b.value ? 1 : a.value < b.value ? -1 : 0));
        h.forEach((c) => {
          if (c.children) this.sortHierarchy(c.children);
        });
      }
    },
    changeTreeFilterText(treeview, val) {
      treeview.tempText = val;
    },
    clearTreeFilters(index) {
      this.clearTreeFilterText(index);
      this.clearHierarchy(this.hierarchies[index].selected);
      this.ignoreTVInputEvent = true;
      
      // Collapse all nodes when clearing filter
      this.$nextTick(() => {
        const ref = this.$refs["tv" + index];
        if (ref && ref.length) {
          ref[0].updateAll(false);
        }
      });
      
      this.doFilter();
    },
    clearTreeFilterText(index) {
      let tv = this.hierarchies[index];
      tv.tempText = "";
      tv.filterText = "";
      if (tv.selected.length === 0) {
        // Collapse all nodes when clearing filter text
        this.$nextTick(() => {
          const ref = this.$refs["tv" + index];
          if (ref && ref.length) {
            ref[0].updateAll(false);
          }
        });
        tv.open.splice(0);
        this.doFilter();
      }
    },
    triggerTreeFilter: function (index) {
      this.hierarchies[index].filterText = this.hierarchies[index].tempText;
      // const ref = this.$refs["tv" + index];
      // if (ref && ref.length) ref[0].updateAll(true);
      this.doFilter();
    },
    filterHryItems(item, search, textKey) {
      const match =
        item[textKey].toLowerCase().indexOf(search.toLowerCase()) > -1;
      if (match && search && search.trim()) {
        // Auto-expand when search results are found
        this.$nextTick(() => {
          // Find the hierarchy that has this search term
          this.hierarchies.forEach((hry, index) => {
            if (hry.filterText && hry.filterText.trim() && 
                hry.filterText.toLowerCase().trim() === search.toLowerCase().trim()) {
              const ref = this.$refs["tv" + index];
              if (ref && ref.length > 0) {
                // Expand all nodes to show search results
                ref[0].updateAll(true);
              }
            }
          });
        });
      }
      return match;
    },
    doFilter() {
      // Debounce rapid filter changes to prevent excessive rebuilds
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(() => {
        // Clear filter cache when data changes - IMPORTANT: Clear before any calculations
        this.clearFilterCache();
        
        // Store current expanded state to preserve it during rebuild
        const currentExpanded = this.expanded;
        
        // Clean up filters that are no longer available
        this.cleanupDocumentFilters();
        
        // Force immediate cache invalidation for document type/status computeds
        this.$forceUpdate();
        
        // Don't clear selections when just filtering - preserve current state
        this.$nextTick(() => {
          this.filterDocuments();
          this.refreshFilterCounts();
          
          // Restore expanded state after rebuild
          this.$nextTick(() => {
            this.expanded = currentExpanded;
          });
        });
      }, 150); // 150ms debounce to prevent excessive calls
    },
    async getAvailableDocTypes() {
      if (this.useIndex)
        return;

      const cacheKey = this.generateFilterCacheKey('excludeDocTypes');
      if (this.filterCache.has(cacheKey)) {
        this.availableDocTypes = this.filterCache.get(cacheKey);
        return;
      }
      
      // Always show ALL document types, but update counts based on current filtering
      const allTypes = Array.from(new Set(this.effectiveItems.map(doc => doc.doc_type).filter(Boolean)));
      const result = allTypes.map(docType => {
        const count = this.getDocTypeCount(docType);
        return { 
          docType, 
          tmpl_id: this.getTemplateIdForDocType(docType),
          count: count,
          disabled: count === 0
        };
      });

      this.filterCache.set(cacheKey, result);
      this.availableDocTypes = result;
    },
    async getAvailableDocStatuses() {
      if (this.useIndex)
        return;

      const cacheKey = this.generateFilterCacheKey('excludeDocStatus');
      if (this.filterCache.has(cacheKey)) {
        this.availableDocStatuses = this.filterCache.get(cacheKey);
        return;
      }

      // Always show ALL document statuses, but update counts based on current filtering
      const allStatuses = Array.from(new Set(this.effectiveItems.map(doc => doc.lifecycle_status_name).filter(Boolean)));
      const result = allStatuses.map(status => {
        const count = this.getDocStatusCount(status);
        return { 
          status,
          count: count,
          disabled: count === 0
        };
      });
      
      this.filterCache.set(cacheKey, result);
      this.availableDocStatuses = result;
    },
    async filterDocuments() {
      // Start with empty array - only include documents that match explicit selections
      let matchingDocs = [];
      
      // Check if we have any hierarchy selections
      const hasHierarchySelections = this.hierarchies.some(hry => hry.selected && hry.selected.length > 0);
      
      // Check if we have any classifier selections
      const hasClassifierSelections = this.classifiers.some(classifier => classifier.selected && classifier.selected.length > 0);
      
      // If no selections at all, return empty array - this ensures explore button shows 0 and is disabled
      if (!hasHierarchySelections && !hasClassifierSelections) {
        this.selectedDocuments = [];
        return;
      }

      if (this.useIndex) {
        const params = {
          ...this.filters,
          returnFields: ['doc_id', 'system_number'],
          page: 1,
          pageSize: 10000 // Large number to get all matching docs
        };
        const pageResult = await this.$store.getters["docs/pagedFilter"](params);
        matchingDocs = pageResult.documents || [];
      } else {
        // Start with all filtered documents only if we have selections
        matchingDocs = [...this.filteredItems];
        
        // Filter by hierarchy selections if any
        if (hasHierarchySelections) {
          // For AND logic, we need to filter documents that match ALL selected hierarchy types
          const hierarchyFilters = [];
          
          this.hierarchies.forEach((hry, hryIndex) => {
            if (hry.selected && hry.selected.length > 0) {
              const hierarchySelectedIds = [];
              hry.selected.forEach((sel, selIndex) => {
                // Ensure sel.id exists and is a string before splitting
                if (!sel || !sel.id || typeof sel.id !== 'string') {
                  return;
                }
                
                // Extract hr_id from the selected node's id to find matching documents
                const hrId = sel.id.split('_')[0]; // Get the base hr_id
                
                // Try matching with hr_id first
                let hierarchyMatchingDocs = this.filteredItems.filter(doc => {
                  const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hry.ht_id);
                  return docHierarchy && docHierarchy.hr_id === hrId;
                });
                
                // If no matches with hr_id, try with node_id
                if (hierarchyMatchingDocs.length === 0) {
                  hierarchyMatchingDocs = this.filteredItems.filter(doc => {
                    const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hry.ht_id);
                    return docHierarchy && docHierarchy.node_id === hrId;
                  });
                }
                
                // If still no matches, try with just the numeric part
                if (hierarchyMatchingDocs.length === 0) {
                  const numericId = parseInt(hrId);
                  hierarchyMatchingDocs = this.filteredItems.filter(doc => {
                    const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hry.ht_id);
                    return docHierarchy && (
                      parseInt(docHierarchy.hr_id) === numericId || 
                      parseInt(docHierarchy.node_id) === numericId
                    );
                  });
                }
                
                hierarchyMatchingDocs.forEach(doc => {
                  if (!hierarchySelectedIds.includes(doc.doc_id)) {
                    hierarchySelectedIds.push(doc.doc_id);
                  }
                });
              });
              
              // Add this hierarchy's filter to the collection
              if (hierarchySelectedIds.length > 0) {
                hierarchyFilters.push({
                  hierarchyType: hry.label,
                  ht_id: hry.ht_id,
                  documentIds: hierarchySelectedIds
                });
              }
            }
          });
          
          // Apply AND logic: documents must match ALL hierarchy filters
          if (hierarchyFilters.length > 0) {
            // Start with all filtered documents, then apply each hierarchy filter as intersection
            hierarchyFilters.forEach((filter, filterIndex) => {
              matchingDocs = matchingDocs.filter(doc => filter.documentIds.includes(doc.doc_id));
            });
          }
        }
        
        // Filter by classifier selections if any
        if (hasClassifierSelections) {
          this.classifiers.forEach(classifier => {
            if (classifier.selected && classifier.selected.length > 0) {
              const selectedClassifierIds = classifier.selected.map(sel => sel.cv_id);
              
              matchingDocs = matchingDocs.filter(doc => {
                if (!doc.classifiers) return false;
                const docClassifier = doc.classifiers.find(dc => dc.ct_id === classifier.ct_id);
                return docClassifier && selectedClassifierIds.includes(docClassifier.cv_id);
              });
            }
          });
        }  
      }

      this.selectedDocuments = matchingDocs;
    },
    
    // Generate cache key for filtering operations
    generateFilterCacheKey(type) {
      const hierarchySelections = this.hierarchies.map(h => ({
        ht_id: h.ht_id,
        selected: h.selected.map(s => s.id).sort()
      }));
      
      const classifierSelections = this.classifiers.map(c => ({
        ct_id: c.ct_id,
        selected: c.selected.map(s => s.cv_id).sort()
      }));
      
      // Include a timestamp component to ensure cache invalidation on filter changes
      const timestamp = Date.now();
      
      const key = JSON.stringify({
        type,
        hierarchies: hierarchySelections,
        classifiers: classifierSelections,
        docTypes: type === 'excludeDocType' ? [] : this.docTypeFilters.slice().sort(),
        docStatuses: type === 'excludeDocStatus' ? [] : this.docStatusFilters.slice().sort(),
        itemsLength: this.effectiveItems.length,
        // Add cache busting for document filter changes
        filterChangeId: `${this.docTypeFilters.length}_${this.docStatusFilters.length}_${timestamp}`
      });
      
      return key;
    },
    
    // Clear filter cache when data changes
    clearFilterCache() {
      this.filterCache.clear();
      this.hierarchyMatchCache.clear();
    },
    
    // Optimized filtering with caching
    getOptimizedFilteredItems(exclude = null) {
      const cacheKey = this.generateFilterCacheKey(exclude);
      if (this.filterCache.has(cacheKey)) {
        return this.filterCache.get(cacheKey);
      }
      
      let items = this.effectiveItems || [];
      
      // Apply hierarchy selections with optimized matching
      const hierarchyFilterIds = this.getOptimizedHierarchyFilterIds();
      if (hierarchyFilterIds.length > 0) {
        // Use Set for O(1) lookup instead of array.includes
        const idSet = new Set(hierarchyFilterIds);
        items = items.filter(doc => idSet.has(doc.doc_id));
      }
      
      // Apply classifier selections
      items = this.applyClassifierFilters(items);
      
      // Apply document filters based on exclude parameter
      if (exclude !== 'excludeDocType' && this.docTypeFilters.length > 0) {
        const docTypeSet = new Set(this.docTypeFilters);
        items = items.filter(doc => docTypeSet.has(doc.doc_type));
      }
      
      if (exclude !== 'excludeDocStatus' && this.docStatusFilters.length > 0) {
        const docStatusSet = new Set(this.docStatusFilters);
        items = items.filter(doc => docStatusSet.has(doc.lifecycle_status_name));
      }
      
      this.filterCache.set(cacheKey, items);
      return items;
    },
    
    // Optimized hierarchy filtering with caching
    getOptimizedHierarchyFilterIds() {
      const hierarchyKey = JSON.stringify(
        this.hierarchies.map(h => ({
          ht_id: h.ht_id,
          selected: h.selected.map(s => s.id).sort()
        }))
      );
      
      if (this.hierarchyMatchCache.has(hierarchyKey)) {
        return this.hierarchyMatchCache.get(hierarchyKey);
      }
      
      const hierarchyFilters = [];
      
      this.hierarchies.forEach((hry) => {
        if (hry.selected && hry.selected.length > 0) {
          const hierarchySelectedIds = new Set();
          
          hry.selected.forEach((sel) => {
            if (!sel || !sel.id || typeof sel.id !== 'string') {
              return;
            }
            
            const hrId = sel.id.split('_')[0];
            const matchingDocs = this.findMatchingDocuments(hrId, hry.ht_id);
            matchingDocs.forEach(doc => hierarchySelectedIds.add(doc.doc_id));
          });
          
          if (hierarchySelectedIds.size > 0) {
            hierarchyFilters.push(Array.from(hierarchySelectedIds));
          }
        }
      });
      
      // Apply AND logic: documents must match ALL hierarchy filters
      let resultIds = [];
      if (hierarchyFilters.length > 0) {
        resultIds = hierarchyFilters[0];
        for (let i = 1; i < hierarchyFilters.length; i++) {
          const filterSet = new Set(hierarchyFilters[i]);
          resultIds = resultIds.filter(id => filterSet.has(id));
        }
      }
      
      this.hierarchyMatchCache.set(hierarchyKey, resultIds);
      return resultIds;
    },
    
    // Optimized document matching with early exit
    findMatchingDocuments(hrId, htId) {
      const cacheKey = `${hrId}_${htId}`;
      if (this.hierarchyMatchCache.has(cacheKey)) {
        return this.hierarchyMatchCache.get(cacheKey);
      }
      
      const matches = [];
      const numericId = parseInt(hrId);
      
      for (const doc of this.effectiveItems) {
        const docHierarchy = doc.hierarchies?.find(h => h.ht_id === htId);
        if (!docHierarchy) continue;
        
        // Check exact string match first (most common case)
        if (docHierarchy.hr_id === hrId) {
          matches.push(doc);
          continue;
        }
        
        // Check node_id match
        if (docHierarchy.node_id === hrId) {
          matches.push(doc);
          continue;
        }
        
        // Check numeric matches only if needed
        if (!isNaN(numericId)) {
          if (parseInt(docHierarchy.hr_id) === numericId || 
              parseInt(docHierarchy.node_id) === numericId) {
            matches.push(doc);
          }
        }
      }
      
      this.hierarchyMatchCache.set(cacheKey, matches);
      return matches;
    },
    
    // Optimized classifier filtering
    applyClassifierFilters(items) {
      const activeClassifiers = this.classifiers.filter(
        classifier => classifier.selected && classifier.selected.length > 0
      );
      
      if (activeClassifiers.length === 0) {
        return items;
      }
      
      return items.filter(doc => {
        if (!doc.classifiers) return false;
        
        return activeClassifiers.every(classifier => {
          const selectedIds = new Set(classifier.selected.map(sel => sel.cv_id));
          const docClassifier = doc.classifiers.find(dc => dc.ct_id === classifier.ct_id);
          return docClassifier && selectedIds.has(docClassifier.cv_id);
        });
      });
    },
    
    // Clean up document filters that are no longer available based on current selections
    cleanupDocumentFilters() {
      // Get available doc types based on current filtering
      const availableDocTypeValues = this.availableDocTypes.map(dt => dt.docType);
      
      // Remove doc type filters that are no longer available
      this.docTypeFilters = this.docTypeFilters.filter(docType => 
        availableDocTypeValues.includes(docType)
      );
      
      // Get available doc statuses based on current filtering
      const availableDocStatusValues = this.availableDocStatuses.map(ds => ds.status);
      
      // Remove doc status filters that are no longer available
      this.docStatusFilters = this.docStatusFilters.filter(status => 
        availableDocStatusValues.includes(status)
      );
    },
    toggleAllDocTypes() {
      if (this.allDocTypesSelected) {
        this.docTypeFilters = [];
      } else {
        this.docTypeFilters = this.availableDocTypes.map((t) => t.docType);
      }
      this.doFilter();
    },
    toggleAllDocStatuses() {
      if (this.allDocStatusesSelected) {
        this.docStatusFilters = [];
      } else {
        this.docStatusFilters = this.availableDocStatuses.map((s) => s.status);
      }
      this.doFilter();
    },
    clearAllFilters() {
      this.docTypeFilters = [];
      this.docStatusFilters = [];
      this.doFilter();
    },
    clearAllHierarchySelections() {
      this.hierarchies.forEach((h) => {
        h.selected = [];
        h.filterText = "";
        h.tempText = "";
      });
      this.classifiers.forEach((c) => {
        c.selected = [];
      });
      // Ensure auto-selection doesn't happen again after manual clear
      this.initialAutoSelectionDone = true;
      this.doFilter();
    },
    emitExploreDocuments() {
      this.$emit('exploreDocuments', this.selectedDocuments);
    },
    
    // Clear all filters and selections
    clearAll() {
      // Clear document filters
      this.docTypeFilters = [];
      this.docStatusFilters = [];
      
      // Clear hierarchy selections and search text
      this.hierarchies.forEach((h) => {
        h.selected = [];
        h.filterText = "";
        h.tempText = "";
      });
      
      // Clear classifier selections
      this.classifiers.forEach((c) => {
        c.selected = [];
      });
      
      // Ensure auto-selection doesn't happen again after manual clear
      this.initialAutoSelectionDone = true;
      this.doFilter();
    },
    
    onExpansionChange(newExpandedIndex) {
      // Prevent expansion changes during treeview interactions
      if (this.preventExpansionChange) {
        return;
      }
      // Allow normal expansion behavior
      this.expanded = newExpandedIndex;
    },
    
    treeviewInput(idx, val) {
      // Prevent expansion panel changes during treeview selection
      this.preventExpansionChange = true;
      
      // Selection handled by v-model, emit filter event if needed
      const hierarchies = JSON.parse(
        JSON.stringify(this.hierarchies, (key, value) =>
          key === "parent" ? undefined : value
        )
      );
      const classifiers = JSON.parse(JSON.stringify(this.classifiers));
      this.$emit("filterChanged", hierarchies, classifiers);
      
      // Re-enable expansion panel changes after a short delay
      this.$nextTick(() => {
        setTimeout(() => {
          this.preventExpansionChange = false;
          this.doFilter();
        }, 100);
      });
    },
    
    // Get documents filtered by hierarchy selections from OTHER hierarchies and classifiers
    getHierarchyFilteredItems(excludeHtId = null) {
      // Start with all effective items, then apply document filters
      let items = this.effectiveItems || [];
      
      // Apply document type and status filters
      if (this.docTypeFilters.length > 0) {
        const docTypeSet = new Set(this.docTypeFilters);
        items = items.filter(doc => docTypeSet.has(doc.doc_type));
      }
      
      if (this.docStatusFilters.length > 0) {
        const docStatusSet = new Set(this.docStatusFilters);
        items = items.filter(doc => docStatusSet.has(doc.lifecycle_status_name));
      }
      
      // Apply hierarchy selections as filters (excluding the current hierarchy being built)
      const hierarchyFilters = [];
      
      this.hierarchies.forEach((hry) => {
        // Skip filtering by this hierarchy if it's the one being excluded
        if (excludeHtId && hry.ht_id === excludeHtId) {
          return;
        }
        
        if (hry.selected && hry.selected.length > 0) {
          const hierarchySelectedIds = [];
          
          hry.selected.forEach((sel) => {
            // Ensure sel.id exists and is a string before splitting
            if (!sel || !sel.id || typeof sel.id !== 'string') {
              return;
            }
            
            // Extract hr_id from the selected node's id to find matching documents
            const hrId = sel.id.split('_')[0]; // Get the base hr_id
            
            // Try matching with hr_id first
            let hierarchyMatchingDocs = this.effectiveItems.filter(doc => {
              const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hry.ht_id);
              return docHierarchy && docHierarchy.hr_id === hrId;
            });
            
            // If no matches with hr_id, try with node_id
            if (hierarchyMatchingDocs.length === 0) {
              hierarchyMatchingDocs = this.effectiveItems.filter(doc => {
                const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hry.ht_id);
                return docHierarchy && docHierarchy.node_id === hrId;
              });
            }
            
            // If still no matches, try with just the numeric part
            if (hierarchyMatchingDocs.length === 0) {
              const numericId = parseInt(hrId);
              hierarchyMatchingDocs = this.effectiveItems.filter(doc => {
                const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hry.ht_id);
                return docHierarchy && (
                  parseInt(docHierarchy.hr_id) === numericId || 
                  parseInt(docHierarchy.node_id) === numericId
                );
              });
            }
            
            hierarchyMatchingDocs.forEach(doc => {
              if (!hierarchySelectedIds.includes(doc.doc_id)) {
                hierarchySelectedIds.push(doc.doc_id);
              }
            });
          });
          
          // Add this hierarchy's filter to the collection
          if (hierarchySelectedIds.length > 0) {
            hierarchyFilters.push({
              hierarchyType: hry.label,
              ht_id: hry.ht_id,
              documentIds: hierarchySelectedIds
            });
          }
        }
      });
      
      // Apply AND logic: documents must match ALL hierarchy filters
      if (hierarchyFilters.length > 0) {
        hierarchyFilters.forEach((filter) => {
          items = items.filter(doc => filter.documentIds.includes(doc.doc_id));
        });
      }
      
      // Apply classifier selections if any
      this.classifiers.forEach(classifier => {
        if (classifier.selected && classifier.selected.length > 0) {
          const selectedClassifierIds = classifier.selected.map(sel => sel.cv_id);
          
          items = items.filter(doc => {
            if (!doc.classifiers) return false;
            const docClassifier = doc.classifiers.find(dc => dc.ct_id === classifier.ct_id);
            return docClassifier && selectedClassifierIds.includes(docClassifier.cv_id);
          });
        }
      });
      
      return items;
    },
    
    // Update classifier counts based on cross-filtering while keeping all items visible
    updateClassifierCounts(classifier) {
      // Get documents filtered by OTHER classifiers and hierarchies (excluding current classifier)
      const filteredDocs = this.getClassifierFilteredItems(classifier.ct_id);
      
      // Reset all counts to 0
      classifier.items.forEach(item => {
        item.count = 0;
        item.disabled = true; // Will be set to false if count > 0
      });
      
      // Recalculate counts based on filtered documents
      filteredDocs.forEach((doc) => {
        if (doc.classifiers) {
          const docClassifier = doc.classifiers.find(dc => dc.ct_id === classifier.ct_id);
          if (docClassifier) {
            const classifierItem = classifier.items.find(item => item.cv_id === docClassifier.cv_id);
            if (classifierItem) {
              classifierItem.count++;
              classifierItem.disabled = false; // Enable since count > 0
            }
          }
        }
      });
    },
    
    // Get documents filtered by OTHER classifiers and hierarchies (excluding current classifier)
    getClassifierFilteredItems(excludeCtId = null) {
      let items = this.effectiveItems || [];
      
      // Apply document type and status filters
      if (this.docTypeFilters.length > 0) {
        const docTypeSet = new Set(this.docTypeFilters);
        items = items.filter(doc => docTypeSet.has(doc.doc_type));
      }
      
      if (this.docStatusFilters.length > 0) {
        const docStatusSet = new Set(this.docStatusFilters);
        items = items.filter(doc => docStatusSet.has(doc.lifecycle_status_name));
      }
      
      // Apply hierarchy selections as filters
      const hierarchyFilterIds = this.getOptimizedHierarchyFilterIds();
      if (hierarchyFilterIds.length > 0) {
        const idSet = new Set(hierarchyFilterIds);
        items = items.filter(doc => idSet.has(doc.doc_id));
      }
      
      // Apply OTHER classifier selections (excluding the current classifier being updated)
      this.classifiers.forEach(classifier => {
        // Skip filtering by this classifier if it's the one being excluded
        if (excludeCtId && classifier.ct_id === excludeCtId) {
          return;
        }
        
        if (classifier.selected && classifier.selected.length > 0) {
          const selectedClassifierIds = new Set(classifier.selected.map(sel => sel.cv_id));
          
          items = items.filter(doc => {
            if (!doc.classifiers) return false;
            const docClassifier = doc.classifiers.find(dc => dc.ct_id === classifier.ct_id);
            return docClassifier && selectedClassifierIds.has(docClassifier.cv_id);
          });
        }
      });
      
      return items;
    },

    async refreshFilterCounts() {

      if (this.useIndex) {
        const allFilters = this.filters;

        const data = [
          ...this.hierarchies.map(ht => ({
            tag: `ht_${ht.ht_id}`,
            filters: {
              hierarchyFilters: allFilters.hierarchyFilters.filter(f => f.ht_id !== ht.ht_id),
              classifierFilters: allFilters.classifierFilters,
              docTypeIncluded: allFilters.docTypeIncluded,
              docStatusIncluded: allFilters.docStatusIncluded,
            }
          })),
          ...this.classifiers.map(c => ({
            tag: `ct_${c.ct_id}`,
            filters: {
              hierarchyFilters: allFilters.hierarchyFilters,
              classifierFilters: allFilters.classifierFilters.filter(f => f.ct_id !== c.ct_id),
              docTypeIncluded: allFilters.docTypeIncluded,
              docStatusIncluded: allFilters.docStatusIncluded,
            }
          })),
          {
            tag: 'doc_status_text',
            filters: {
              hierarchyFilters: allFilters.hierarchyFilters,
              classifierFilters: allFilters.classifierFilters,
              docTypeIncluded: allFilters.docTypeIncluded,
            }
          },
          {
            tag: 'doc_type',
            filters: {
              hierarchyFilters: allFilters.hierarchyFilters,
              classifierFilters: allFilters.classifierFilters,
              docStatusIncluded: allFilters.docStatusIncluded,
            }
          }
        ];
        
        await this.$store.getters['docs/getCountsByAttr'](data).then(resp => {
          resp.forEach(r => {
            if (r.tag === 'doc_type') {
              this.availableDocTypes = this.docTypeSummary.map(dt => {
                const count = r.values.find(c => c.value === dt.docType)?.count || 0;
                return { 
                  docType: dt.docType,
                  tmpl_id: this.getTemplateIdForDocType(dt),
                  count: count,
                  disabled: count === 0
                };
              });
            } else if (r.tag === 'doc_status_text') {
              this.availableDocStatuses = this.docStatusSummary.map(s => {
                const count = r.values.find(c => c.value === s.status)?.count || 0;
                return { 
                  status: s.status,
                  count: count,
                  disabled: count === 0
                };
              });
            } else if (r.tag.startsWith('ht_')) {
              const htId = Number(r.tag.replace('ht_', ''));
              const hierarchy = this.hierarchies.find(h => h.ht_id === htId);
              
              this.resetHierarchyCounts(hierarchy.items);
              r.values.forEach(v => {
                const docHierarchy = this.hierarchyLookup[`h${v.value}`];
                if (docHierarchy)
                  this.incrementHierarchyCounts(hierarchy.items, hierarchy.cols, docHierarchy, v.count);
              });

              this.updateHierarchyDisplay(hierarchy.items);
            } else if (r.tag.startsWith('ct_')) {
              const ctId = Number(r.tag.replace('ct_', ''));
              const classifier = this.classifiers.find(c => c.ct_id === ctId);

              if (!classifier) return;

              classifier.items.forEach(item => {
                item.count = 0;
                item.disabled = true;
              });
              
              r.values.forEach((v) => {
                const docClassifier = this.docClassifierLookup[`c${v.value}`] ?? { cv_id: v.value, value: "UNCLASSIFIED" };
                if (docClassifier) {
                  const classifierItem = classifier.items.find(item => item.cv_id === docClassifier.cv_id);
                  if (classifierItem) {
                    classifierItem.count = v.count;
                    classifierItem.disabled = false; // Enable since count > 0
                  } else {
                    classifier.items.push({
                      cv_id: docClassifier.cv_id,
                      value: docClassifier.value,
                      count: v.count,
                      disabled: false
                    });
                  }
                }
              });

              classifier.items.sort((a, b) => (a.value > b.value ? 1 : a.value < b.value ? -1 : 0) );
            }

          })
        }).catch(e => {
          console.log(e);
        })
      } else {
        this.hierarchies.forEach(h => {
          // Now update counts based on cross-hierarchy filtering (excluding current hierarchy)
          this.updateHierarchyCounts(h);
        })

        this.getAvailableDocTypes();
        this.getAvailableDocStatuses();
      }
    },
    
    // Update hierarchy counts based on cross-filtering while keeping all items visible
    updateHierarchyCounts(hierarchy) {
      if (this.useIndex)
        return;
      
      // Get documents filtered by OTHER hierarchies (excluding current hierarchy)
      const filteredDocs = this.getHierarchyFilteredItems(hierarchy.ht_id);
      
      // Reset all counts to 0 and mark as disabled
      this.resetHierarchyCounts(hierarchy.items);
      
      // Recalculate counts based on filtered documents
      filteredDocs.forEach((doc) => {
        const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hierarchy.ht_id);
        if (docHierarchy) {
          this.incrementHierarchyCounts(hierarchy.items, hierarchy.cols, docHierarchy, 1);
        }
      });

      // Update display names and disabled states
      this.updateHierarchyDisplay(hierarchy.items);
    },
    
    // Reset all counts to 0 and mark items as potentially disabled
    resetHierarchyCounts(items) {
      items.forEach(item => {
        item.count = 0;
        item.disabled = true; // Will be set to false if count > 0
        if (item.children && item.children.length > 0) {
          this.resetHierarchyCounts(item.children);
        }
      });
    },
    
    // Increment counts for matching hierarchy paths
    incrementHierarchyCounts(topitem, levels, item, count) {
      let current = topitem;
      levels.forEach((l, i) => {
        const value = utils.removeTags(item[levels[i]] || "UNCLASSIFIED").trim();
        const h = current.find((item) => item.value === value);
        if (h) {
          h.count += count;
          h.disabled = false; // Enable since count > 0
        }
        current = h?.children || [];
      });
    },
    
    // Update display names and disabled states for all hierarchy items
    updateHierarchyDisplay(items) {
      items.forEach(item => {
        // Update display name with count
        item.name = item.value + " (" + item.count + ")";
        
        // If this item has children, update them recursively
        if (item.children && item.children.length > 0) {
          this.updateHierarchyDisplay(item.children);
        }
      });
    },
    
    findItemInHierarchy(items, targetId) {
      // Recursively search for an item by ID in the hierarchy tree
      for (const item of items) {
        if (item.id === targetId) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const found = this.findItemInHierarchy(item.children, targetId);
          if (found) {
            return found;
          }
        }
      }
      return null;
    },
    findItemInHierarchyByHrId(items, targetHrId) {
      // Recursively search for LEAF NODES ONLY by hr_id in the hierarchy tree
      for (const item of items) {
        // Ensure item has valid structure before processing
        if (!item || typeof item !== 'object') {
          continue;
        }
        
        // Only match leaf nodes (bottom level items without children)
        if (item.isLeaf && item.hr_id) {
          // Try exact match first
          if (item.hr_id === targetHrId || item.hr_id === String(targetHrId)) {
            return item;
          }
          
          // Try numeric comparison
          if (parseInt(item.hr_id) === parseInt(targetHrId)) {
            return item;
          }
        }
        
        // Continue searching in children for leaf nodes
        if (item.children && item.children.length > 0) {
          const found = this.findItemInHierarchyByHrId(item.children, targetHrId);
          if (found) {
            return found;
          }
        }
      }
      
      return null;
    },

    findHierarchyItemsForDocuments(hierarchy) {
      // Auto-select hierarchy items based on the documents that were passed in via items prop
      const autoSelectedItems = []
      const uniqueBottomLevelHrIds = new Set()
      
      // Use this.items (passedParams documents) for auto-selection, not effectiveItems
      if (this.items && this.items.length > 0) {
        this.items.forEach((doc, docIndex) => {
          const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hierarchy.ht_id)
          if (docHierarchy) {
            // Extract ONLY the bottom level (leaf) hierarchy ID
            // The hierarchy path is typically structured as: root > parent > child > leaf
            // We want only the deepest/bottom level hr_id
            const bottomLevelHrId = this.getBottomLevelHierarchyId(docHierarchy)
            if (bottomLevelHrId) {
              uniqueBottomLevelHrIds.add(bottomLevelHrId)
            }
          }
        })
      }
      
      // Find corresponding items in the hierarchy tree for ONLY the bottom-level hr_ids
      uniqueBottomLevelHrIds.forEach(hrId => {
        // Look for items with matching base hr_id
        const found = this.findItemInHierarchyByHrId(hierarchy.items, hrId)
        if (found) {
          autoSelectedItems.push(found)
        }
      })
      
      return autoSelectedItems
    },

    // Helper method to extract the bottom-level (leaf) hierarchy ID from a document's hierarchy
    getBottomLevelHierarchyId(docHierarchy) {
      // The document hierarchy typically contains the full path from root to leaf
      // We need to identify the deepest/most specific hierarchy level
      
      // If hr_id is the direct leaf node, use it
      if (docHierarchy.hr_id) {
        // This is likely the bottom level ID we want
        return docHierarchy.hr_id
      }
      
      // If there's a hierarchy path or array, get the last/deepest one
      if (docHierarchy.path && Array.isArray(docHierarchy.path) && docHierarchy.path.length > 0) {
        return docHierarchy.path[docHierarchy.path.length - 1].hr_id
      }
      
      // If there are child hierarchies, get the deepest one
      if (docHierarchy.children && Array.isArray(docHierarchy.children) && docHierarchy.children.length > 0) {
        // Recursively find the deepest child
        return this.getBottomLevelHierarchyId(docHierarchy.children[docHierarchy.children.length - 1])
      }
      return null
    },

    // Debug helper to log hierarchy structure
    debugHierarchyStructure(items, level = 0) {
      const indent = '  '.repeat(level);
      const structure = [];
      
      items.forEach(item => {
        structure.push(`${indent}${item.value} (id: ${item.id}, hr_id: ${item.hr_id}, isLeaf: ${item.isLeaf})`);
        if (item.children && item.children.length > 0) {
          structure.push(...this.debugHierarchyStructure(item.children, level + 1));
        }
      });
      
      return structure.join('\n');
    },
    
    // Helper method to get template ID for a document type
    getTemplateIdForDocType(docType) {
      // Look through effective items to find a document with this doc_type and return its tmpl_id
      const doc = (this.effectiveItems || []).find(item => item.doc_type === docType);
      return doc ? doc.tmpl_id : null;
    },
    
    // Set initial document type filter based on framework tmpl_id
    setInitialDocTypeFilter() {
      if (!this.framework || !this.framework.tmpl_id) {
        return;
      }
      
      // Only set if docTypeFilters is currently empty (don't override user selections)
      if (this.docTypeFilters.length > 0) {
        return;
      }
      
      // Find document type that matches the framework's tmpl_id
      const matchingDocType = this.availableDocTypes.find(docType => {
        const doc = (this.effectiveItems || []).find(item => 
          item.doc_type === docType.docType && item.tmpl_id === this.framework.tmpl_id
        );
        return !!doc;
      });
      
      if (matchingDocType) {
        this.docTypeFilters = [matchingDocType.docType];
        // Trigger filtering to update the hierarchy display
        this.$nextTick(() => {
          this.doFilter();
        });
      }
    },
    
    // Get count of documents for a specific document type based on current filtering
    getDocTypeCount(docType) {
      // Get filtered items excluding document type filters
      const filteredItems = this.getOptimizedFilteredItems('excludeDocType');
      return filteredItems.filter(doc => doc.doc_type === docType).length;
    },
    
    // Get count of documents for a specific document status based on current filtering
    getDocStatusCount(status) {
      // Get filtered items excluding document status filters
      const filteredItems = this.getOptimizedFilteredItems('excludeDocStatus');
      return filteredItems.filter(doc => doc.lifecycle_status_name === status).length;
    },
    
    // Clear individual classifier selection
    clearClassifierSelection(index) {
      this.classifiers[index].selected = [];
      this.doFilter();
    },
    
    // Clear individual hierarchy selection
    clearHierarchySelection(index) {
      this.hierarchies[index].selected = [];
      this.hierarchies[index].filterText = "";
      this.hierarchies[index].tempText = "";
      this.doFilter();
    },
    
    // Clear document type filters
    clearDocTypeFilters() {
      this.docTypeFilters = [];
      this.doFilter();
    },
    
    // Clear document status filters
    clearDocStatusFilters() {
      this.docStatusFilters = [];
      this.doFilter();
    },
    
    // Get count of documents for a specific hierarchy's selections
    getHierarchyDocumentCount(hierarchy) {
      if (!hierarchy.selected || hierarchy.selected.length === 0) {
        return 0;
      }

      if (this.useIndex) {
        return hierarchy.selected.length;
      } else {
      
        // Get all documents that match this hierarchy's selections
        const hierarchySelectedIds = [];
        
        hierarchy.selected.forEach((sel) => {
          if (!sel || !sel.id || typeof sel.id !== 'string') {
            return;
          }
          
          // Extract hr_id from the selected node's id to find matching documents
          const hrId = sel.id.split('_')[0];
          
          // Find matching documents using the same logic as selectedDocuments
          let hierarchyMatchingDocs = this.filteredItems.filter(doc => {
            const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hierarchy.ht_id);
            return docHierarchy && docHierarchy.hr_id === hrId;
          });
          
          // If no matches with hr_id, try with node_id
          if (hierarchyMatchingDocs.length === 0) {
            hierarchyMatchingDocs = this.filteredItems.filter(doc => {
              const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hierarchy.ht_id);
              return docHierarchy && docHierarchy.node_id === hrId;
            });
          }
          
          // If still no matches, try with just the numeric part
          if (hierarchyMatchingDocs.length === 0) {
            const numericId = parseInt(hrId);
            hierarchyMatchingDocs = this.filteredItems.filter(doc => {
              const docHierarchy = doc.hierarchies?.find(h => h.ht_id === hierarchy.ht_id);
              return docHierarchy && (
                parseInt(docHierarchy.hr_id) === numericId || 
                parseInt(docHierarchy.node_id) === numericId
              );
            });
          }
          
          hierarchyMatchingDocs.forEach(doc => {
            if (!hierarchySelectedIds.includes(doc.doc_id)) {
              hierarchySelectedIds.push(doc.doc_id);
            }
          });
        });
        
        return hierarchySelectedIds.length;
      }
    },
  }
}
</script>

<style scoped>
.hryContainer {
  padding: 0px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  position: relative;
}

/* Scrollable Content Area */
.scrollable-content {
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 0px 1px 0px 0px;
  margin-bottom: 80px; /* Space for fixed footer */
}

/* Fixed Footer with Action Buttons */
.fixed-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  padding: 12px;
  flex-shrink: 0;
}

.footer-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
}

/* Explore Skills Bar */
.explore-skill-bar {
  margin-bottom: 12px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.explore-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.clear-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.explore-btn {
  font-weight: 600;
  letter-spacing: 0.025em;
  border-radius: 8px;
  padding: 4px 16px;
  height: 34px;
  text-transform: none;
  font-size: 13px;
  flex-shrink: 0;
}

.explore-btn:disabled {
  opacity: 0.6;
}

.explore-btn:focus {
  outline: none !important;
  box-shadow: none !important;
}

.clear-btn {
  font-weight: 600;
  letter-spacing: 0.025em;
  border-radius: 8px;
  padding: 0 16px;
  height: 34px;
  text-transform: none;
  font-size: 13px;
  flex-shrink: 0;
}

.clear-btn:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.clear-btn:disabled {
  opacity: 0.4;
}

.clear-btn:focus {
  outline: none !important;
  box-shadow: none !important;
}

.selection-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
}

/* Filter Panel */
.filter-panel {
  background: #ffffff;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
  overflow: hidden;
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.sub-header-style-1 {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filters-toggle-btn {
  font-weight: 500;
  color: #475569;
  transition: all 0.2s ease;
  font-size: 12px;
}

.filters-toggle-btn:hover {
  color: #1976d2;
  background-color: rgba(25, 118, 210, 0.04);
}

/* Filters button tab-like appearance when expanded */
.filters-expanded {
  height: 40px !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  margin-bottom: -8px !important;
  align-items: flex-start !important;
  padding-top: 4px !important;
}

.filters-section {
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  border-top-right-radius: 0px;
  margin-top: -4px !important;
}

.filter-row {
  margin: 0;
}

.filter-col {
  padding: 0 6px;
}

.filter-group {
  margin-bottom: 12px;
}

.filter-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.doc-types-filter,
.doc-statuses-filter {
  margin-bottom: 0;
}

.doc-types-filter ::v-deep .v-input__control,
.doc-statuses-filter ::v-deep .v-input__control {
  border-radius: 6px;
  min-height: 32px;
}

.doc-types-filter ::v-deep .v-select__slot,
.doc-statuses-filter ::v-deep .v-select__slot {
  border-radius: 6px;
  min-height: 32px;
}

.selection-text {
  font-size: 12px;
  color: #475569;
  font-weight: 500;
}

.select-all-item {
  transition: background-color 0.2s ease;
  min-height: 36px;
}

.select-all-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.filter-summary {
  padding-top: 8px;
  margin-top: 8px;
}

.filter-summary-text {
  font-size: 12px;
  color: #6b7280;
}

.clear-filters-link {
  color: #2563eb;
  background: none;
  border: none;
  text-decoration: underline;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  margin: 0;
}

.clear-filters-link:hover {
  color: #1e40af;
}

.clear-filters-btn {
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 11px;
}

.clear-filters-btn:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

/* Expansion Panels */
::v-deep .v-expansion-panels {
  box-shadow: none;
}

::v-deep .v-expansion-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

::v-deep .v-expansion-panel-header {
  padding: 0 !important;
  background: #ffffff;
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  min-height: 40px;
}

::v-deep .v-expansion-panel--active .v-expansion-panel-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

::v-deep .v-expansion-panel-header:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

/* Hide the default expansion panel icon completely */
::v-deep .v-expansion-panel-header__icon {
  display: none !important;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 12px 16px;
  overflow-y: auto;
  margin-bottom: 1px;
}

/* Full-width header layout */
.full-width-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
}

.chevron-icon {
  font-size: 18px !important;
  color: #AAAAAA;
  transition: transform 0.2s ease, color 0.2s ease;
}

.chevron-icon.rotated {
  transform: rotate(90deg);
}

::v-deep .v-expansion-panel--active .chevron-icon {
  color: #000000;
}

.header-title {
  font-weight: 400;
  color: #000000;
  font-size: 14px;
  transition: font-weight 0.2s ease;
}

::v-deep .v-expansion-panel--active .header-title {
  font-weight: 700;
  font-style: bold;
}

.clear-selection-btn {
  box-sizing: border-box;
  display: flex !important;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 2px 8px !important;
  gap: 4px;
  margin: 0 auto;
  min-width: 89px !important;
  height: 24px !important;
  background: #EEEEEE !important;
  border: 1px solid #EEEEEE !important;
  border-radius: 8px !important;
  flex: none;
  order: 1;
  flex-grow: 0;
  font-size: 10px !important;
  font-weight: 500;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: #666666 !important;
}

.clear-selection-btn:before {
  display: none !important;
  background-color: #EEEEEE;
}

.clear-icon {
  margin-right: 2px !important;
  font-size: 18px !important;
  color: #666666 !important;
  padding-left: 2px !important;
}

.count-badge {
  background-color: #FFF !important;
  color: #000000 !important;
  font-size: 9px !important;
  height: 16px !important;
  min-width: 16px !important;
  padding: 0 4px !important;
  margin: 0 4px !important;
  border-radius: 8px !important;
  left: 2px;
  bottom: 1px;
}

.count-badge ::v-deep .v-chip__content {
  padding: 0 !important;
  line-height: 18px !important;
  font-weight: 600 !important;
  padding-left: 1px !important;
  padding-right: 2px !important;
}

/* Treeview Styling */
.treeview0,
.treeview1,
.treeview2,
.treeview3,
.treeview4,
.treeview5 {
  margin-left: 0;
}

::v-deep .v-treeview-node__root {
  padding: 2px 0;
}

::v-deep .v-treeview-node__content {
  padding: 4px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  min-height: 28px;
}

::v-deep .v-treeview-node__content:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

::v-deep .v-treeview-node__checkbox .v-icon {
  color: #1976d2;
  font-size: 18px;
}

::v-deep .v-treeview-node__label {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

::v-deep .v-treeview-node--selected > .v-treeview-node__content {
  background-color: rgba(25, 118, 210, 0.08);
  border-left: 2px solid #1976d2;
}

/* Disabled hierarchy items styling */
.disabled-hierarchy-item {
  color: #9ca3af !important;
  opacity: 0.6;
  text-decoration: line-through;
}

::v-deep .v-treeview-node--disabled .v-treeview-node__checkbox .v-icon {
  color: #d1d5db !important;
  opacity: 0.5;
}

::v-deep .v-treeview-node--disabled .v-treeview-node__content {
  opacity: 0.6;
  cursor: not-allowed;
}

::v-deep .v-treeview-node--disabled .v-treeview-node__content:hover {
  background-color: transparent !important;
}

/* Disabled filter items styling */
.disabled-filter-item ::v-deep .v-label {
  color: #9ca3af !important;
  opacity: 0.6;
  text-decoration: line-through;
}

.disabled-filter-item ::v-deep .v-input--selection-controls__input .v-icon {
  color: #d1d5db !important;
  opacity: 0.5;
}

.disabled-filter-item:hover {
  background-color: transparent !important;
  cursor: not-allowed;
}

/* Filter Checkbox Lists */
.filter-checkbox-list {
  padding: 0;
  margin: 0;
}

.filter-checkbox-item {
  margin-bottom: 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.filter-checkbox-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.filter-checkbox-item ::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
  min-height: 28px;
}

.filter-checkbox-item ::v-deep .v-input--selection-controls__input {
  margin-right: 8px;
  height: 28px;
  align-items: baseline;
  justify-content: flex-start;
}

.filter-checkbox-item ::v-deep .v-input__slot {
  padding: 4px 6px;
  margin-bottom: 0;
}

.filter-checkbox-item ::v-deep .v-label {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  line-height: 1.2;
  padding-left: 0;
}

.filter-checkbox-item ::v-deep .v-input--selection-controls__ripple {
  border-radius: 4px;
}

.filter-checkbox-item ::v-deep .v-icon {
  font-size: 24px;
}

.filter-checkbox-item ::v-deep .v-input--is-focused .v-input__slot::before,
.filter-checkbox-item ::v-deep .v-input--is-focused .v-input__slot::after {
  display: none;
}

.filter-checkbox-item ::v-deep .v-messages {
  display: none;
}

/* Selected state styling to match treeview */
.filter-checkbox-item ::v-deep .v-input--is-dirty {
  background-color: rgba(25, 118, 210, 0.08);
  border-left: 2px solid #1976d2;
  margin-left: -2px;
  padding-left: 8px;
}

/* Text Field Filters */
.hierarchy-filter-container {
  margin-bottom: 12px;
  padding: 0 4px;
}

.hierarchy-filter-input {
  margin-bottom: 0 !important;
}

.hierarchy-filter-input ::v-deep .v-input__control {
  min-height: 32px !important;
  border-radius: 6px !important;
}

.hierarchy-filter-input ::v-deep .v-text-field__details {
  display: none !important;
}

.hierarchy-filter-input ::v-deep .v-input__prepend-inner {
  margin-top: 6px !important;
  margin-right: 4px !important;
}

.hierarchy-filter-input ::v-deep .v-input__append-inner {
  margin-top: 6px !important;
  margin-left: 4px !important;
}

.hierarchy-filter-input ::v-deep .v-input__icon--append .v-icon {
  font-size: 18px !important;
  color: #64748b !important;
}

.hierarchy-filter-input ::v-deep .v-input__icon--append .v-icon:hover {
  color: #dc2626 !important;
}

.hierarchy-filter-input ::v-deep .v-text-field__slot input {
  font-size: 13px !important;
  padding: 8px 0 !important;
}

.hierarchy-filter-input ::v-deep .v-text-field__slot input::placeholder {
  color: #9ca3af !important;
  font-size: 13px !important;
}

::v-deep .v-text-field--outlined fieldset {
  border-radius: 6px;
}

::v-deep .v-text-field--outlined .v-input__control {
  border-radius: 6px;
  min-height: 32px;
}

::v-deep .v-text-field .v-input__control {
  min-height: 32px;
}

/* Animations */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

/* Loading State */
::v-deep .v-progress-circular {
  margin: 20px auto;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .explore-controls {
    flex-wrap: wrap;
    gap: 6px;
  }
  
  .clear-buttons {
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .clear-btn {
    font-size: 10px;
    padding: 0 8px;
    height: 26px;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .explore-controls {
    flex-wrap: wrap;
    gap: 6px;
  }
  
  .clear-buttons {
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .clear-btn {
    font-size: 10px;
    padding: 0 8px;
    height: 26px;
  }
}

@media (max-width: 768px) {
  .hryContainer {
    padding: 8px;
  }
  
  .explore-skill-bar {
    padding: 8px;
  }
  
  .explore-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .clear-buttons {
    justify-content: center;
    width: 100%;
  }
  
  .explore-btn {
    width: 100%;
    justify-content: center;
  }
  
  .clear-btn {
    flex: 1;
    min-width: 0;
    font-size: 10px;
    padding: 0 8px;
  }
  
  .selection-summary {
    justify-content: center;
    margin-top: 4px;
  }
  
  .filters-header {
    padding: 10px 12px;
  }
  
  .filters-section {
    padding: 10px 12px;
  }
  
  .filter-col {
    padding: 4px 0;
  }
  
  ::v-deep .v-expansion-panel-header {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  ::v-deep .v-expansion-panel-content__wrap {
    padding: 10px 12px;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .hryContainer {
    padding: 6px;
  }
  
  .explore-skill-bar {
    padding: 6px;
  }
  
  .clear-btn {
    font-size: 9px;
    height: 26px;
    padding: 0 6px;
  }
  
  .explore-btn {
    height: 36px;
    font-size: 12px;
  }
  
  .sub-header-style-1 {
    font-size: 13px;
  }
  
  .filters-toggle-btn {
    font-size: 11px;
  }
}

/* Focus States for Accessibility */
::v-deep .v-btn:focus:not(.explore-btn):not(.clear-btn) {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

::v-deep .v-select:focus-within .v-input__control {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

::v-deep .v-text-field:focus-within .v-input__control {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Utility classes from cSkillJobView */
.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.font-medium {
  font-weight: 500;
}

.text-gray-700 {
  color: #374151;
}

.text-gray-600 {
  color: #4b5563;
}

.text-blue-600 {
  color: #2563eb;
}

.hover\:text-blue-800:hover {
  color: #1e40af;
}

.underline {
  text-decoration-line: underline;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style>
